import React, { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface AdmissionFormProps {
  domain: string;
}

const AdmissionForm: React.FC<AdmissionFormProps> = ({ domain }) => {
  const [formData, setFormData] = useState({
    studentName: '',
    fatherName: '',
    motherName: '',
    dateOfBirth: '',
    gender: '',
    appliedClass: '',
    email: '',
    phone: '',
    alternatePhone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    previousSchool: '',
    previousClass: '',
    nationality: 'Indian',
    religion: '',
    caste: '',
    bloodGroup: '',
    medicalConditions: '',
  });

  const [submitting, setSubmitting] = useState(false);

  const classes = ['Nursery', 'LKG', 'UKG', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const generateApplicationId = () => {
    const year = new Date().getFullYear();
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `ADM${year}${random}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.studentName || !formData.fatherName || !formData.dateOfBirth || !formData.appliedClass || !formData.phone || !formData.email) {
      alert('Please fill all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const applicationId = generateApplicationId();

      const applicationData = {
        applicationId,
        ...formData,
        status: 'pending',
        appliedOn: new Date().toISOString(),
      };

      const applicationsRef = collection(db, `school-data/${domain}/applications`);
      await addDoc(applicationsRef, applicationData);

      alert(`Application submitted successfully!\nApplication ID: ${applicationId}`);

      // Reset form
      setFormData({
        studentName: '',
        fatherName: '',
        motherName: '',
        dateOfBirth: '',
        gender: '',
        appliedClass: '',
        email: '',
        phone: '',
        alternatePhone: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        previousSchool: '',
        previousClass: '',
        nationality: 'Indian',
        religion: '',
        caste: '',
        bloodGroup: '',
        medicalConditions: '',
      });
    } catch (error: any) {
      console.error('Error submitting application:', error);
      alert(`Failed to submit application: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0">
          <i className="bi bi-file-earmark-plus me-2 text-primary-custom"></i>
          New Admission Application
        </h5>
      </div>

      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <h6 className="mb-3 text-primary-custom">Student Information</h6>
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <label className="form-label">Student Name *</label>
              <input
                type="text"
                className="form-control"
                name="studentName"
                value={formData.studentName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Date of Birth *</label>
              <input
                type="date"
                className="form-control"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Gender *</label>
              <select
                className="form-select"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                required
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Applied Class *</label>
              <select
                className="form-select"
                name="appliedClass"
                value={formData.appliedClass}
                onChange={handleChange}
                required
              >
                <option value="">Select Class</option>
                {classes.map((cls) => (
                  <option key={cls} value={cls}>Class {cls}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Nationality</label>
              <input
                type="text"
                className="form-control"
                name="nationality"
                value={formData.nationality}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Religion</label>
              <input
                type="text"
                className="form-control"
                name="religion"
                value={formData.religion}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Blood Group</label>
              <select
                className="form-select"
                name="bloodGroup"
                value={formData.bloodGroup}
                onChange={handleChange}
              >
                <option value="">Select Blood Group</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
              </select>
            </div>
          </div>

          <h6 className="mb-3 text-primary-custom">Parent Information</h6>
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <label className="form-label">Father's Name *</label>
              <input
                type="text"
                className="form-control"
                name="fatherName"
                value={formData.fatherName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Mother's Name</label>
              <input
                type="text"
                className="form-control"
                name="motherName"
                value={formData.motherName}
                onChange={handleChange}
              />
            </div>
          </div>

          <h6 className="mb-3 text-primary-custom">Contact Information</h6>
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <label className="form-label">Email *</label>
              <input
                type="email"
                className="form-control"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Phone *</label>
              <input
                type="tel"
                className="form-control"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Alternate Phone</label>
              <input
                type="tel"
                className="form-control"
                name="alternatePhone"
                value={formData.alternatePhone}
                onChange={handleChange}
              />
            </div>
            <div className="col-12">
              <label className="form-label">Address</label>
              <textarea
                className="form-control"
                name="address"
                rows={2}
                value={formData.address}
                onChange={handleChange}
              ></textarea>
            </div>
            <div className="col-md-4">
              <label className="form-label">City</label>
              <input
                type="text"
                className="form-control"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">State</label>
              <input
                type="text"
                className="form-control"
                name="state"
                value={formData.state}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Pincode</label>
              <input
                type="text"
                className="form-control"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
              />
            </div>
          </div>

          <h6 className="mb-3 text-primary-custom">Previous Education</h6>
          <div className="row g-3 mb-4">
            <div className="col-md-8">
              <label className="form-label">Previous School</label>
              <input
                type="text"
                className="form-control"
                name="previousSchool"
                value={formData.previousSchool}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Previous Class</label>
              <input
                type="text"
                className="form-control"
                name="previousClass"
                value={formData.previousClass}
                onChange={handleChange}
              />
            </div>
          </div>

          <h6 className="mb-3 text-primary-custom">Additional Information</h6>
          <div className="row g-3 mb-4">
            <div className="col-12">
              <label className="form-label">Medical Conditions / Allergies</label>
              <textarea
                className="form-control"
                name="medicalConditions"
                rows={3}
                value={formData.medicalConditions}
                onChange={handleChange}
                placeholder="Please mention any medical conditions, allergies, or special needs"
              ></textarea>
            </div>
          </div>

          <div className="d-flex gap-2">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Submitting...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-2"></i>
                  Submit Application
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setFormData({
                studentName: '',
                fatherName: '',
                motherName: '',
                dateOfBirth: '',
                gender: '',
                appliedClass: '',
                email: '',
                phone: '',
                alternatePhone: '',
                address: '',
                city: '',
                state: '',
                pincode: '',
                previousSchool: '',
                previousClass: '',
                nationality: 'Indian',
                religion: '',
                caste: '',
                bloodGroup: '',
                medicalConditions: '',
              })}
            >
              <i className="bi bi-x-circle me-2"></i>
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdmissionForm;
